// src/pages/AdminPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getDashboardStats, getAllOrders, getProducts,
  addProduct, updateProduct, deleteProduct,
  updateOrderStatus, getAllUsers, updateUserRole, uploadImage
} from '../firebase/services';
import Toast from '../components/Toast';

const TABS = ['Dashboard', 'Products', 'Orders', 'Users'];

export default function AdminPage() {
  const { user, userData } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('Dashboard');
  const [toast, setToast] = useState(null);

  // Dashboard state
  const [stats, setStats] = useState(null);

  // Products state
  const [products, setProducts] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '', price: '', originalPrice: '', category: '',
    type: 'food', description: '', image: '', stock: 10,
    featured: false, isVeg: true, prepTime: '', calories: '',
    sizes: '', colors: '', tags: '',
  });
  const [imgUploading, setImgUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileRef = useRef();

  // Orders state
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderFilter, setOrderFilter] = useState('all');

  // Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  useEffect(() => {
    if (!user || (userData && userData.role !== 'admin')) {
      navigate('/');
    }
  }, [user, userData, navigate]);

  useEffect(() => {
    if (tab === 'Dashboard') {
      getDashboardStats().then(setStats).catch(() => {});
    } else if (tab === 'Products') {
      loadProducts();
    } else if (tab === 'Orders') {
      loadOrders();
    } else if (tab === 'Users') {
      loadUsers();
    }
  }, [tab]);

  const loadProducts = async () => {
    setProductLoading(true);
    getProducts().then(setProducts).finally(() => setProductLoading(false));
  };
  const loadOrders = async () => {
    setOrdersLoading(true);
    getAllOrders().then(setOrders).finally(() => setOrdersLoading(false));
  };
  const loadUsers = async () => {
    setUsersLoading(true);
    getAllUsers().then(setUsers).finally(() => setUsersLoading(false));
  };

  const sp = (k, v) => setProductForm(f => ({ ...f, [k]: v }));

  const resetProductForm = () => {
    setProductForm({ name: '', price: '', originalPrice: '', category: '', type: 'food', description: '', image: '', stock: 10, featured: false, isVeg: true, prepTime: '', calories: '', sizes: '', colors: '', tags: '' });
    setEditingProduct(null);
    setShowProductForm(false);
  };

  const handleEditProduct = (p) => {
    setProductForm({
      name: p.name || '', price: p.price || '', originalPrice: p.originalPrice || '',
      category: p.category || '', type: p.type || 'food',
      description: p.description || '', image: p.image || '',
      stock: p.stock || 10, featured: p.featured || false,
      isVeg: p.isVeg !== undefined ? p.isVeg : true,
      prepTime: p.prepTime || '', calories: p.calories || '',
      sizes: (p.sizes || []).join(', '), colors: (p.colors || []).join(', '),
      tags: (p.tags || []).join(', '),
    });
    setEditingProduct(p);
    setShowProductForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImgUploading(true);
    try {
      const url = await uploadImage(file, `products/${Date.now()}_${file.name}`, setUploadProgress);
      sp('image', url);
      setToast({ message: 'Image uploaded!', type: 'success' });
    } catch {
      setToast({ message: 'Upload failed', type: 'error' });
    } finally { setImgUploading(false); setUploadProgress(0); }
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...productForm,
        price: Number(productForm.price),
        originalPrice: productForm.originalPrice ? Number(productForm.originalPrice) : null,
        stock: Number(productForm.stock),
        calories: productForm.calories ? Number(productForm.calories) : null,
        sizes: productForm.sizes ? productForm.sizes.split(',').map(s => s.trim()).filter(Boolean) : [],
        colors: productForm.colors ? productForm.colors.split(',').map(s => s.trim()).filter(Boolean) : [],
        tags: productForm.tags ? productForm.tags.split(',').map(s => s.trim()).filter(Boolean) : [],
      };
      if (editingProduct) {
        await updateProduct(editingProduct.id, data);
        setToast({ message: 'Product updated!', type: 'success' });
      } else {
        await addProduct(data);
        setToast({ message: 'Product added!', type: 'success' });
      }
      resetProductForm();
      loadProducts();
    } catch (err) {
      setToast({ message: err.message || 'Save failed', type: 'error' });
    }
  };

  const handleDeleteProduct = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await deleteProduct(id);
      setToast({ message: 'Product deleted', type: 'info' });
      loadProducts();
    } catch {
      setToast({ message: 'Delete failed', type: 'error' });
    }
  };

  // Full status flow matching your Firestore statusHistory schema
  const STATUS_FLOW = {
    'Created':          { mainStatus: 'Order Placed',     description: 'Order placed successfully.' },
    'Accepted':         { mainStatus: 'Preparing Order',  description: 'Preparing Order' },
    'Ready':            { mainStatus: 'Ready for Pickup', description: 'Ready for Pickup' },
    'Order Picked':     { mainStatus: 'Picked',           description: 'Order picked up by delivery partner.' },
    'Out for Delivery': { mainStatus: 'On the Way',       description: 'Out for delivery.' },
    'Delivered':        { mainStatus: 'Completed',        description: 'Order delivered successfully.' },
    'Cancelled':        { mainStatus: 'Cancelled',        description: 'Order has been cancelled.' },
  };

  const handleOrderStatus = async (docId, newStatus) => {
    const flow = STATUS_FLOW[newStatus];
    if (!flow) return;
    try {
      await updateOrderStatus(
        docId,
        newStatus,
        flow.mainStatus,
        flow.description,
        user.uid   // updatedBy = admin uid
      );
      setToast({ message: `✓ Status updated → ${flow.mainStatus}`, type: 'success' });
      loadOrders();
    } catch {
      setToast({ message: 'Update failed', type: 'error' });
    }
  };

  const handleRoleChange = async (uid, role) => {
    try {
      await updateUserRole(uid, role);
      setToast({ message: `Role updated to ${role}`, type: 'success' });
      loadUsers();
    } catch {
      setToast({ message: 'Role update failed', type: 'error' });
    }
  };

  const filteredOrders = orderFilter === 'all'
    ? orders
    : orders.filter(o => (o.status || '') === orderFilter);

  // Status options matching your real Firestore statusHistory values
  const STATUS_OPTS = ['Created', 'Accepted', 'Ready', 'Order Picked', 'Out for Delivery', 'Delivered', 'Cancelled'];
  const STATUS_COLORS = {
    'Created':          '#7AABF0',
    'Accepted':         '#D4A853',
    'Ready':            '#A78BFA',
    'Order Picked':     '#3bb77e',
    'Out for Delivery': '#f97316',
    'Delivered':        '#4CAF7D',
    'Cancelled':        '#D45353',
  };
  const MAIN_STATUS = {
    'Created':          'Order Placed',
    'Accepted':         'Preparing Order',
    'Ready':            'Ready for Pickup',
    'Order Picked':     'Picked',
    'Out for Delivery': 'On the Way',
    'Delivered':        'Completed',
    'Cancelled':        'Cancelled',
  };

  const cardStyle = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '24px 28px' };
  const labelStyle = { display: 'block', fontSize: 10, fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 8 };
  const thStyle = { padding: '12px 16px', fontSize: 10, fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--gold)', textAlign: 'left', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '14px 16px', fontSize: 12, color: 'var(--text-2)', borderBottom: '1px solid var(--border)', verticalAlign: 'middle' };

  return (
    <div style={{ paddingTop: 68, minHeight: '100vh' }}>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* Admin header */}
      <div style={{ background: 'var(--bg-2)', borderBottom: '1px solid var(--border)', padding: '32px 40px' }}>
        <div className="container" style={{ padding: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <p className="section-eyebrow">Control Panel</p>
              <h1 className="section-title" style={{ fontSize: 'clamp(1.6rem, 3vw, 2.4rem)' }}>Admin Dashboard</h1>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {TABS.map(t => (
                <button key={t} onClick={() => setTab(t)} style={{
                  padding: '9px 20px', fontSize: 11, fontWeight: 500,
                  letterSpacing: '0.1em', textTransform: 'uppercase',
                  background: tab === t ? 'var(--gold)' : 'transparent',
                  color: tab === t ? '#09080A' : 'var(--text-2)',
                  border: `1px solid ${tab === t ? 'var(--gold)' : 'var(--border)'}`,
                  borderRadius: 3, cursor: 'pointer', transition: 'all 0.25s',
                }}>{t}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="container" style={{ padding: '40px 40px' }}>

        {/* ═══ DASHBOARD TAB ═══ */}
        {tab === 'Dashboard' && (
          <div>
            {!stats ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 20, marginBottom: 32 }}>
                {[...Array(5)].map((_, i) => <div key={i} className="skeleton" style={{ height: 110, borderRadius: 8 }} />)}
              </div>
            ) : (
              <>
                {/* Stat cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 20, marginBottom: 40 }}>
                  {[
                    { label: 'Total Revenue', value: `$${stats.totalRevenue.toFixed(2)}`, icon: '💰', color: 'var(--gold)' },
                    { label: 'Total Orders', value: stats.totalOrders, icon: '📦', color: '#7AABF0' },
                    { label: 'Products', value: stats.totalProducts, icon: '🛍', color: '#A78BFA' },
                    { label: 'Customers', value: stats.totalUsers, icon: '👥', color: 'var(--emerald-3)' },
                    { label: 'Pending Orders', value: stats.pendingOrders, icon: '⏳', color: '#D4A853' },
                  ].map(s => (
                    <div key={s.label} style={{ ...cardStyle, textAlign: 'center' }}>
                      <div style={{ fontSize: 32, marginBottom: 12 }}>{s.icon}</div>
                      <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 28, color: s.color, marginBottom: 6 }}>{s.value}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Recent orders */}
                <div style={cardStyle}>
                  <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 20 }}>Recent Orders</div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          {['Order ID', 'Items', 'Total', 'Status', 'Date'].map(h => (
                            <th key={h} style={thStyle}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(stats.recentOrders || []).map(o => (
                          <tr key={o.id} style={{ transition: 'background 0.2s' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}
                          >
                            <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--gold)' }}>#{o.id.slice(0,8).toUpperCase()}</span></td>
                            <td style={tdStyle}>{(o.items || []).length}</td>
                            <td style={{ ...tdStyle, fontFamily: 'Playfair Display, serif', color: 'var(--gold)' }}>${(o.total || 0).toFixed(2)}</td>
                            <td style={tdStyle}>
                              <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 10, fontWeight: 500, background: `${STATUS_COLORS[o.status] || '#888'}18`, color: STATUS_COLORS[o.status] || '#888', border: `1px solid ${STATUS_COLORS[o.status] || '#888'}30`, textTransform: 'capitalize' }}>
                                {o.status}
                              </span>
                            </td>
                            <td style={tdStyle}>{o.createdAt?.toDate ? o.createdAt.toDate().toLocaleDateString() : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ═══ PRODUCTS TAB ═══ */}
        {tab === 'Products' && (
          <div>
            {/* Product Form */}
            {showProductForm && (
              <div style={{ ...cardStyle, marginBottom: 32, animation: 'fade-up 0.3s ease' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                  <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 22 }}>
                    {editingProduct ? 'Edit Product' : 'Add New Product'}
                  </h3>
                  <button onClick={resetProductForm} style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 22 }}>×</button>
                </div>

                <form onSubmit={handleSaveProduct}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 16 }}>
                    <div>
                      <label style={labelStyle}>Product Name *</label>
                      <input className="input" value={productForm.name} onChange={e => sp('name', e.target.value)} required placeholder="e.g. Wagyu Burger" />
                    </div>
                    <div>
                      <label style={labelStyle}>Type *</label>
                      <select className="input" value={productForm.type} onChange={e => sp('type', e.target.value)}>
                        <option value="food">Food</option>
                        <option value="shopping">Shopping</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Category *</label>
                      <input className="input" value={productForm.category} onChange={e => sp('category', e.target.value)} required placeholder="e.g. Burgers / Fashion" />
                    </div>
                    <div>
                      <label style={labelStyle}>Price ($) *</label>
                      <input className="input" type="number" min="0" step="0.01" value={productForm.price} onChange={e => sp('price', e.target.value)} required />
                    </div>
                    <div>
                      <label style={labelStyle}>Original Price ($)</label>
                      <input className="input" type="number" min="0" step="0.01" value={productForm.originalPrice} onChange={e => sp('originalPrice', e.target.value)} placeholder="Leave blank if no sale" />
                    </div>
                    <div>
                      <label style={labelStyle}>Stock</label>
                      <input className="input" type="number" min="0" value={productForm.stock} onChange={e => sp('stock', e.target.value)} />
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Description</label>
                    <textarea className="input" rows={3} value={productForm.description} onChange={e => sp('description', e.target.value)} style={{ resize: 'vertical' }} />
                  </div>

                  {/* Image */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Product Image</label>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <input className="input" value={productForm.image} onChange={e => sp('image', e.target.value)} placeholder="Paste image URL or upload below" style={{ flex: 1 }} />
                      <button type="button" onClick={() => fileRef.current?.click()} className="btn btn-outline btn-sm" style={{ flexShrink: 0 }} disabled={imgUploading}>
                        {imgUploading ? `${Math.round(uploadProgress)}%` : '⬆ Upload'}
                      </button>
                      <input type="file" accept="image/*" ref={fileRef} onChange={handleImageUpload} style={{ display: 'none' }} />
                    </div>
                    {productForm.image && (
                      <img src={productForm.image} alt="Preview" style={{ marginTop: 12, width: 100, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)' }} />
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 16 }}>
                    <div>
                      <label style={labelStyle}>Sizes (comma-separated)</label>
                      <input className="input" value={productForm.sizes} onChange={e => sp('sizes', e.target.value)} placeholder="XS, S, M, L, XL" />
                    </div>
                    <div>
                      <label style={labelStyle}>Colors (comma-separated)</label>
                      <input className="input" value={productForm.colors} onChange={e => sp('colors', e.target.value)} placeholder="Black, White, Gold" />
                    </div>
                    <div>
                      <label style={labelStyle}>Tags (comma-separated)</label>
                      <input className="input" value={productForm.tags} onChange={e => sp('tags', e.target.value)} placeholder="luxury, new, trending" />
                    </div>
                  </div>

                  {productForm.type === 'food' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 16 }}>
                      <div>
                        <label style={labelStyle}>Prep Time</label>
                        <input className="input" value={productForm.prepTime} onChange={e => sp('prepTime', e.target.value)} placeholder="20 min" />
                      </div>
                      <div>
                        <label style={labelStyle}>Calories</label>
                        <input className="input" type="number" value={productForm.calories} onChange={e => sp('calories', e.target.value)} placeholder="520" />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 24 }}>
                        <label style={{ fontSize: 12, color: 'var(--text-2)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input type="checkbox" checked={productForm.isVeg} onChange={e => sp('isVeg', e.target.checked)} style={{ accentColor: 'var(--emerald-3)' }} />
                          Vegetarian
                        </label>
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                    <label style={{ fontSize: 12, color: 'var(--text-2)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" checked={productForm.featured} onChange={e => sp('featured', e.target.checked)} style={{ accentColor: 'var(--gold)' }} />
                      Mark as Featured
                    </label>
                  </div>

                  <div style={{ display: 'flex', gap: 12 }}>
                    <button type="submit" className="btn btn-gold">
                      {editingProduct ? '✓ Update Product' : '+ Add Product'}
                    </button>
                    <button type="button" onClick={resetProductForm} className="btn btn-outline">Cancel</button>
                  </div>
                </form>
              </div>
            )}

            {/* Products list header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 22 }}>
                All Products <span style={{ fontSize: 16, color: 'var(--text-3)', fontFamily: 'DM Sans, sans-serif' }}>({products.length})</span>
              </div>
              {!showProductForm && (
                <button onClick={() => setShowProductForm(true)} className="btn btn-gold btn-sm">+ Add Product</button>
              )}
            </div>

            {productLoading ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                {[...Array(6)].map((_, i) => <div key={i} className="skeleton" style={{ height: 90, borderRadius: 8 }} />)}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                  <thead>
                    <tr style={{ background: 'var(--surface-2)' }}>
                      {['Product', 'Type', 'Category', 'Price', 'Stock', 'Rating', 'Actions'].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {products.map(p => (
                      <tr key={p.id}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                        style={{ transition: 'background 0.15s' }}
                      >
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ width: 48, height: 48, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)', flexShrink: 0 }}>
                              <img src={p.image} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{p.name}</div>
                              {p.featured && <span style={{ fontSize: 9, color: 'var(--gold)' }}>✦ Featured</span>}
                            </div>
                          </div>
                        </td>
                        <td style={tdStyle}>
                          <span className={`badge ${p.type === 'food' ? 'badge-emerald' : 'badge-gold'}`} style={{ fontSize: 9 }}>
                            {p.type === 'food' ? 'Food' : 'Shop'}
                          </span>
                        </td>
                        <td style={tdStyle}>{p.category}</td>
                        <td style={{ ...tdStyle, fontFamily: 'Playfair Display, serif', color: 'var(--gold)' }}>
                          ${p.price}
                          {p.originalPrice && <span style={{ fontSize: 11, color: 'var(--text-3)', textDecoration: 'line-through', marginLeft: 6 }}>${p.originalPrice}</span>}
                        </td>
                        <td style={{ ...tdStyle, color: p.stock < 5 ? 'var(--red)' : 'var(--text-2)' }}>{p.stock}</td>
                        <td style={tdStyle}>
                          <span className="stars" style={{ fontSize: 11 }}>{'★'.repeat(Math.round(p.rating || 0))}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 4 }}>({p.reviewCount || 0})</span>
                        </td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => handleEditProduct(p)} className="btn btn-outline btn-sm" style={{ padding: '5px 12px', fontSize: 10 }}>Edit</button>
                            <button onClick={() => handleDeleteProduct(p.id, p.name)} style={{ padding: '5px 12px', fontSize: 10, background: 'rgba(212,83,83,0.1)', border: '1px solid rgba(212,83,83,0.3)', color: 'var(--red)', borderRadius: 3, cursor: 'pointer', transition: 'all 0.2s' }}>Del</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ═══ ORDERS TAB ═══ */}
        {tab === 'Orders' && (
          <div>
            {/* Filter pills */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
              {['all', ...STATUS_OPTS].map(f => {
                const count = f === 'all' ? orders.length : orders.filter(o => o.status === f).length;
                const col   = STATUS_COLORS[f] || 'var(--gold)';
                const active = orderFilter === f;
                return (
                  <button key={f} onClick={() => setOrderFilter(f)} style={{
                    padding: '7px 14px', fontSize: 10, fontWeight: 600,
                    letterSpacing: '0.08em',
                    background: active ? (f === 'all' ? 'var(--gold)' : `${col}20`) : 'transparent',
                    color: active ? (f === 'all' ? '#09080A' : col) : 'var(--text-3)',
                    border: `1px solid ${active ? (f === 'all' ? 'var(--gold)' : col) : 'var(--border)'}`,
                    borderRadius: 20, cursor: 'pointer', transition: 'all 0.2s',
                  }}>
                    {f === 'all' ? 'All' : f} <span style={{ opacity: 0.7 }}>({count})</span>
                  </button>
                );
              })}
            </div>

            {ordersLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[...Array(5)].map((_, i) => <div key={i} className="skeleton" style={{ height: 90, borderRadius: 8 }} />)}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                  <thead>
                    <tr style={{ background: 'var(--surface-2)' }}>
                      {['Order ID', 'Customer', 'Vendor', 'Items', 'Total', 'Payment', 'Status', 'Date', 'Update Status'].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map(o => {
                      const statusColor = STATUS_COLORS[o.status] || '#888';
                      const itemCount   = (o.orderDetails || o.items || []).length;
                      const total       = o.totalAmount || o.total || 0;
                      const custName    = o.customerName || `${o.shippingAddress?.firstName || ''} ${o.shippingAddress?.lastName || ''}`.trim() || '—';
                      const custPhone   = o.phoneNumber  || o.shippingAddress?.phone || '';
                      const orderId     = o.OrderId      || o.id;
                      const isCOD       = (o.paymentMethod || '').toUpperCase() === 'COD';
                      const date        = o.createdAt?.toDate ? o.createdAt.toDate() : null;

                      return (
                        <tr key={o.id}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}
                          style={{ transition: 'background 0.15s' }}
                        >
                          {/* Order ID */}
                          <td style={tdStyle}>
                            <div style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--gold)', fontWeight: 700 }}>
                              {orderId || `#${o.id.slice(0,8).toUpperCase()}`}
                            </div>
                            {isCOD && (
                              <span style={{ fontSize: 9, background: '#fde68a', color: '#92400e', padding: '1px 6px', borderRadius: 10, fontWeight: 700, marginTop: 3, display: 'inline-block' }}>COD</span>
                            )}
                          </td>

                          {/* Customer */}
                          <td style={tdStyle}>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>{custName}</div>
                            <div style={{ fontSize: 10, color: 'var(--text-3)' }}>📞 {custPhone}</div>
                          </td>

                          {/* Vendor */}
                          <td style={tdStyle}>
                            <div style={{ fontSize: 11, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {o.vendorName || '—'}
                            </div>
                          </td>

                          {/* Items */}
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            <span style={{ fontSize: 12, fontWeight: 600 }}>{itemCount}</span>
                          </td>

                          {/* Total */}
                          <td style={{ ...tdStyle, fontFamily: 'Playfair Display, serif', color: 'var(--gold)', whiteSpace: 'nowrap' }}>
                            ₹{total.toFixed(2)}
                          </td>

                          {/* Payment method */}
                          <td style={tdStyle}>
                            <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, background: isCOD ? '#fde68a30' : '#d1fae530', color: isCOD ? '#92400e' : '#065f46', border: `1px solid ${isCOD ? '#fde68a' : '#a7f3d0'}`, fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {o.paymentMethod || '—'}
                            </span>
                          </td>

                          {/* Current status badge */}
                          <td style={tdStyle}>
                            <div>
                              <span style={{ padding: '3px 9px', borderRadius: 12, fontSize: 9, fontWeight: 700, background: `${statusColor}20`, color: statusColor, border: `1px solid ${statusColor}40`, whiteSpace: 'nowrap', display: 'inline-block', marginBottom: 2 }}>
                                {o.status || '—'}
                              </span>
                              <div style={{ fontSize: 9, color: 'var(--text-3)', marginTop: 1 }}>
                                {o.mainStatus || MAIN_STATUS[o.status] || ''}
                              </div>
                            </div>
                          </td>

                          {/* Date */}
                          <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontSize: 11 }}>
                            {date ? (
                              <>
                                <div>{date.toLocaleDateString('en-IN')}</div>
                                <div style={{ fontSize: 10, color: 'var(--text-3)' }}>{date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                              </>
                            ) : '—'}
                          </td>

                          {/* Update status dropdown */}
                          <td style={tdStyle}>
                            <select
                              value={o.status || ''}
                              onChange={e => handleOrderStatus(o.FirestoreDocId || o.id, e.target.value)}
                              style={{
                                background: 'var(--surface-2)',
                                border: '1px solid var(--border)',
                                color: 'var(--text)', padding: '6px 10px',
                                borderRadius: 6, fontSize: 11, cursor: 'pointer',
                                minWidth: 140,
                              }}
                            >
                              {STATUS_OPTS.map(s => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredOrders.length === 0 && (
                      <tr>
                        <td colSpan={9} style={{ ...tdStyle, textAlign: 'center', padding: '48px', color: 'var(--text-3)' }}>
                          <div style={{ fontSize: 36, marginBottom: 12 }}>📦</div>
                          No orders found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ═══ USERS TAB ═══ */}
        {tab === 'Users' && (
          <div>
            <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 22, marginBottom: 20 }}>
              All Users <span style={{ fontSize: 16, color: 'var(--text-3)', fontFamily: 'DM Sans, sans-serif' }}>({users.length})</span>
            </div>

            {usersLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[...Array(5)].map((_, i) => <div key={i} className="skeleton" style={{ height: 64, borderRadius: 8 }} />)}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                  <thead>
                    <tr style={{ background: 'var(--surface-2)' }}>
                      {['User', 'Email', 'Phone', 'Role', 'Joined', 'Orders', 'Change Role'].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                        style={{ transition: 'background 0.15s' }}
                      >
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg, var(--gold), var(--gold-3))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Playfair Display, serif', fontSize: 14, color: '#09080A', flexShrink: 0 }}>
                              {(u.displayName || u.email || 'U')[0].toUpperCase()}
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{u.displayName || 'No name'}</span>
                          </div>
                        </td>
                        <td style={tdStyle}>{u.email}</td>
                        <td style={tdStyle}>{u.phone || '—'}</td>
                        <td style={tdStyle}>
                          <span className={`badge ${u.role === 'admin' ? 'badge-gold' : 'badge-emerald'}`} style={{ fontSize: 9 }}>
                            {u.role === 'admin' ? '⚙ Admin' : '✦ Customer'}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          {u.createdAt?.toDate ? u.createdAt.toDate().toLocaleDateString('en-IN') : '—'}
                        </td>
                        <td style={tdStyle}>{(u.orders || []).length}</td>
                        <td style={tdStyle}>
                          {u.id !== user?.uid ? (
                            <select
                              value={u.role || 'customer'}
                              onChange={e => handleRoleChange(u.id, e.target.value)}
                              style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '5px 10px', borderRadius: 3, fontSize: 11, cursor: 'pointer' }}
                            >
                              <option value="customer">Customer</option>
                              <option value="admin">Admin</option>
                            </select>
                          ) : (
                            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>You</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
